/**
 * Row editors on the item page that change more than one field: categories
 * (primary category and altitude through updateItem, additional categories
 * through the frame actions) and triage (the same submitTriageAssessment call
 * ItemTriageActions makes).
 */
import { useState } from 'react';
import { TextInput } from '@/components/ds';
import { btnPrimary, btnSecondary } from '@/components/mono/primitives';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTriageStore } from '@/stores/prioritization/triageStore';
import { CANONICAL_CATEGORIES } from '@/lib/cellAllocation';
import { getItemCategoryLabel } from '@/components/prioritization/TrackCellBadge';
import { createEmptyQuickChecks, type TriageOutcome } from '@/types/prioritization/triage';
import type { Item, ItemFrame, ItemLanguageType } from '@/types/prioritization/lifecycle';
import { ScopingQuestionnaire } from '@/components/prioritization/leveling';
import { computeAltitudeLevel } from '@/lib/altitudePrediction';
import { effortFieldsFromWorkSize } from '@/lib/itemToPlanningAdapter';
import { usePlanningDefaultsStore } from '@/stores/prioritization/planningDefaultsStore';
import { useAltitudeText, type AltitudeText } from '@/hooks/prioritization/useAltitudeText';
import type { AltitudeScopingInputs } from '@/types/prioritization/leveling';
import { CreateFrameModal } from '@/components/prioritization/frames';
import { InfoModal } from '@/components/prioritization/InfoModal';
import { ALTITUDE_GUIDE_SECTIONS, CATEGORY_GUIDE_SECTIONS } from '@/components/prioritization/item-view/itemClassificationGuides';
import { ChoiceList } from '@/components/shared/ChoiceList';
import { EditorError } from './ItemDetailThirdPassView';
import { useEditorSave } from '@/components/detail-page/useEditorSave';

function categoryLabel(raw: string): string {
  return getItemCategoryLabel(raw) ?? raw;
}

function frameType(f: ItemFrame): string {
  return f.frameType ?? (f as unknown as { frame_type?: string }).frame_type ?? '';
}

const CATEGORY_OPTIONS = CANONICAL_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }));
// The picker is labelled Altitude, so each choice is the number and its tag: "3 · Epic".
const altitudeOptions = (altitudeText: AltitudeText, category: string) =>
  ([1, 2, 3, 4, 5] as const).map((n) => ({ value: String(n), label: altitudeText.format(n, category || undefined, { short: true }) ?? String(n) }));
const LABEL = 'font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-dim)]';

export function CategoriesEditor({ item, close, save }: { item: Item; close: () => void; save: (u: Partial<Item>) => Promise<string | null> }) {
  const altitudeText = useAltitudeText();
  const addFrame = useItemsStore((s) => s.addFrame);
  const updateFrame = useItemsStore((s) => s.updateFrame);
  const removeFrame = useItemsStore((s) => s.removeFrame);
  const setCanonicalFrame = useItemsStore((s) => s.setCanonicalFrame);
  const setUseItemAsCanonical = useItemsStore((s) => s.setUseItemAsCanonical);
  // The category shown as primary is the active frame's when one is chosen, as on the earlier page.
  const activeType = (item.frames ?? []).find((f) => f.id === item.canonicalFrameId);
  const effectiveCategory = (activeType ? frameType(activeType) : item.itemCategory) ?? '';
  const [category, setCategory] = useState<string>(effectiveCategory);
  const [reframeTo, setReframeTo] = useState<ItemLanguageType | null>(null);
  const [guide, setGuide] = useState<'category' | 'altitude' | null>(null);
  const [altitude, setAltitude] = useState<string>(item.altitudeLevel ? String(item.altitudeLevel) : '');
  const frames = item.frames ?? [];
  const [drafts, setDrafts] = useState<Record<string, { type: string; title: string; altitude: string }>>(() =>
    Object.fromEntries(frames.map((f) => [f.id, { type: frameType(f), title: f.title ?? '', altitude: f.altitudeLevel ? String(f.altitudeLevel) : '' }])),
  );
  const [newFrame, setNewFrame] = useState({ type: '', title: '', altitude: '' });
  const bandHours = usePlanningDefaultsStore((s) => s.defaults.itemEffortBandHours);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quiz, setQuiz] = useState<AltitudeScopingInputs>(() => (item.altitudeScopingInputs ?? {}) as AltitudeScopingInputs);
  const [quizUsed, setQuizUsed] = useState(false);
  const { saving, error, run } = useEditorSave(async () => {
    const updates: Partial<Item> = {};
    // Changing the primary category keeps the old one (the earlier page's rule): with no category yet it is
    // set; going back to the item's own category or to an existing additional one makes that one active;
    // a new category opens the reframe dialog, which adds it as a category and keeps the old wording.
    let reframe: ItemLanguageType | null = null;
    if (category !== effectiveCategory) {
      const existing = frames.find((f) => frameType(f) === category);
      if (!item.itemCategory || !category) updates.itemCategory = (category || undefined) as ItemLanguageType | undefined;
      else if (category === item.itemCategory) setUseItemAsCanonical(item.id);
      else if (existing) setCanonicalFrame(item.id, existing.id);
      else reframe = category as ItemLanguageType;
    }
    const alt = altitude ? (Number(altitude) as Item['altitudeLevel']) : undefined;
    if (alt !== (item.altitudeLevel ?? undefined)) updates.altitudeLevel = alt;
    if (quizUsed) {
      // Same record the old altitude quiz kept: the answers, a proposed altitude, and effort seeded from work size.
      updates.altitudeScopingInputs = quiz as Item['altitudeScopingInputs'];
      updates.altitudeLifecycleState = 'proposed';
      const fromWorkSize = quiz.workSize ? effortFieldsFromWorkSize(quiz.workSize, item.effortSource, bandHours) : null;
      if (fromWorkSize) Object.assign(updates, fromWorkSize);
    }
    if (Object.keys(updates).length > 0) {
      const err = await save(updates);
      if (err) return err;
    }
    for (const f of frames) {
      const d = drafts[f.id];
      if (!d) continue;
      if (d.type !== frameType(f) || d.title !== (f.title ?? '') || d.altitude !== (f.altitudeLevel ? String(f.altitudeLevel) : '')) {
        updateFrame(item.id, f.id, { frameType: d.type as ItemLanguageType, title: d.title, altitudeLevel: d.altitude || undefined });
      }
    }
    if (newFrame.type) {
      addFrame(item.id, {
        frameType: newFrame.type as ItemLanguageType,
        title: newFrame.title || item.title,
        description: item.description ?? '',
        createdBy: 'user',
        isCanonical: false,
        altitudeLevel: newFrame.altitude || undefined,
      });
    }
    if (reframe) {
      setReframeTo(reframe);
      return 'keep-open';
    }
    return null;
  }, close);

  return (
    <div
      className="space-y-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={LABEL}>Primary category</span>
          <button {...btnSecondary('!h-7 px-2.5')} onClick={() => setGuide('category')}>
            What do these mean?
          </button>
        </div>
        <div className="mt-1.5">
          <ChoiceList label="Primary category" options={CATEGORY_OPTIONS} value={category} onPick={setCategory} compact />
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={LABEL}>Altitude</span>
          <span className="flex gap-2">
            <button {...btnSecondary('!h-7 px-2.5')} onClick={() => setGuide('altitude')}>
              What do these mean?
            </button>
            <button {...btnSecondary('!h-7 px-2.5')} onClick={() => setQuizOpen((o) => !o)}>
              {quizOpen ? 'Hide the questions' : 'Not sure? Answer three questions'}
            </button>
          </span>
        </div>
        <div className="mt-1.5">
          <ChoiceList label="Altitude" options={altitudeOptions(altitudeText, category)} value={altitude} onPick={setAltitude} compact />
        </div>
        {quizOpen && (
          <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--rule)' }}>
            <ScopingQuestionnaire
              value={quiz}
              candidateType={(category || undefined) as ItemLanguageType | undefined}
              compact
              onChange={(inputs) => {
                setQuiz(inputs);
                setQuizUsed(true);
                const level = computeAltitudeLevel(inputs);
                if (level) setAltitude(String(level));
              }}
              onDirectAltitude={(level) => setAltitude(String(level))}
            />
          </div>
        )}
      </div>
      <div className="text-[13.5px] text-[var(--text-dim)]">Additional categories say the same item another way:</div>
      {frames.map((f) => {
        const d = drafts[f.id] ?? { type: frameType(f), title: f.title ?? '', altitude: '' };
        const set = (patch: Partial<typeof d>) => setDrafts((cur) => ({ ...cur, [f.id]: { ...d, ...patch } }));
        return (
          <div key={f.id} className="space-y-2 border-l-2 pl-3" style={{ borderColor: 'var(--rule-bright)' }}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-[var(--text-pri)]">{categoryLabel(d.type)}</span>
              <button {...btnSecondary('!h-7 px-2.5')} onClick={() => removeFrame(item.id, f.id)} disabled={saving}>
                Remove
              </button>
            </div>
            <TextInput aria-label="Wording" value={d.title} onChange={(e) => set({ title: e.target.value })} />
            <ChoiceList label={`Altitude as ${categoryLabel(d.type)}`} options={altitudeOptions(altitudeText, d.type)} value={d.altitude} onPick={(v) => set({ altitude: v })} compact />
          </div>
        );
      })}
      <div className="space-y-2">
        <div className={LABEL}>Add a category</div>
        <ChoiceList
          label="Add a category"
          options={CATEGORY_OPTIONS.filter((o) => o.value !== category && !frames.some((f) => frameType(f) === o.value))}
          value={newFrame.type}
          onPick={(v) => setNewFrame((n) => ({ ...n, type: n.type === v ? '' : v }))}
          compact
        />
        {newFrame.type && (
          <>
            <TextInput aria-label="Wording for the added category" value={newFrame.title} placeholder="How it reads in that category" onChange={(e) => setNewFrame((n) => ({ ...n, title: e.target.value }))} />
            <ChoiceList label="Altitude for the added category" options={altitudeOptions(altitudeText, newFrame.type)} value={newFrame.altitude} onPick={(v) => setNewFrame((n) => ({ ...n, altitude: v }))} compact />
          </>
        )}
      </div>
      <EditorError error={error === 'keep-open' ? null : error} />
      <InfoModal
        isOpen={guide === 'category'}
        onClose={() => setGuide(null)}
        title="Item categories"
        subtitle="Choose the category that best describes what it is"
        sections={CATEGORY_GUIDE_SECTIONS}
        footerNote="An item has one active category; additional categories describe it another way."
      />
      <InfoModal
        isOpen={guide === 'altitude'}
        onClose={() => setGuide(null)}
        title="Altitude levels"
        subtitle="Altitude describes the scope and organizational elevation of an item"
        sections={ALTITUDE_GUIDE_SECTIONS}
        footerNote="Not sure? The three questions — time horizon, work size and impact surface — work it out."
      />
      <CreateFrameModal
        candidateId={item.id}
        candidateTitle={item.title}
        candidateDescription={item.description}
        currentType={item.itemCategory}
        initialType={reframeTo ?? undefined}
        isOpen={reframeTo !== null}
        onClose={() => setReframeTo(null)}
        onCreated={() => {
          setReframeTo(null);
          close();
        }}
      />
      <div className="flex gap-2">
        <button {...btnPrimary()} onClick={() => void run()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button {...btnSecondary()} onClick={close} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const TRIAGE_CHOICES: { outcome: TriageOutcome; label: string }[] = [
  { outcome: 'pursue', label: 'Pursue' },
  { outcome: 'defer', label: 'Defer' },
  { outcome: 'reject', label: 'Reject' },
  { outcome: 'needs_clarification', label: 'Needs info' },
];

/** The same call ItemTriageActions makes (submitTriageAssessment), drawn as a row editor. */
export function TriageEditor({ itemId, close }: { itemId: string; close: () => void }) {
  const submitTriageAssessment = useTriageStore((s) => s.submitTriageAssessment);
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<TriageOutcome | null>(null);
  const { saving, error, run } = useEditorSave(async () => {
    if (!picked) return 'Choose Pursue, Defer, Reject or Needs info.';
    await submitTriageAssessment(itemId, { itemId, quickChecks: createEmptyQuickChecks(), outcome: picked, notes: note || undefined });
    return null;
  }, close);
  return (
    <div
      className="space-y-2"
      onKeyDown={(e) => {
        if (e.key === 'Escape') close();
      }}
    >
      <div className="flex flex-wrap gap-2">
        {TRIAGE_CHOICES.map((c) => (
          <button
            key={c.outcome}
            {...btnSecondary()}
            aria-pressed={picked === c.outcome}
            style={picked === c.outcome ? { borderColor: 'var(--slate)', color: 'var(--text-pri)', background: 'var(--slate-dim)' } : btnSecondary().style}
            onClick={() => setPicked(c.outcome)}
            disabled={saving}
          >
            {c.label}
          </button>
        ))}
      </div>
      <TextInput
        aria-label="Triage note"
        value={note}
        placeholder="Note (optional)"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void run();
          }
        }}
      />
      <EditorError error={error} />
      <div className="flex gap-2">
        <button {...btnPrimary()} onClick={() => void run()} disabled={saving || !picked}>
          {saving ? 'Saving…' : 'Save decision'}
        </button>
        <button {...btnSecondary()} onClick={close} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

